// 事件地图（瓜田核心数据）—— 均为虚构示意案例，用于信息梳理演示

export type TimelineItem = {
  time: string;
  title: string;
  detail: string;
  tag: "公告" | "网友讨论" | "媒体报道" | "回应";
};

export type KeyPerson = {
  name: string;
  role: string;
  desc: string;
};

export type Perspective = {
  side: string;
  points: string[];
};

export type NetizenVoice = {
  nick: string;
  text: string;
  likes: string;
  attitude: "挺方" | "反方" | "中立";
};

export type SourceCheck = {
  verified: string[];
  unverified: string[];
};

export type EventData = {
  slug: string;
  title: string;
  categorySlug: string;
  category: string;
  heat: number;
  status: "持续发酵" | "基本落地" | "热度回落" | "等待回应";
  statusTone: "hot" | "cool" | "normal";
  readingMinutes: number;
  updated: string;
  summary: string;
  cover: string;
  coverAlt: string;
  tags: string[];
  timeline: TimelineItem[];
  people: KeyPerson[];
  perspectives: Perspective[];
  netizens: NetizenVoice[];
  sourceCheck: SourceCheck;
  related: string[];
};

export const events: EventData[] = [
  {
    slug: "banxing-guochao",
    title: "「半醒」联名「云梯」：国潮包装争议全记录",
    categorySlug: "shangzhan",
    category: "品牌商战",
    heat: 96,
    status: "持续发酵",
    statusTone: "hot",
    readingMinutes: 7,
    updated: "2026-09-07",
    summary:
      "新中式茶饮「半醒」与老牌零食「云梯」的联名礼盒一上架就引发热议，讨论点从「包装创意归属」一路滑向「国潮定价」。我们把各方说法按时间排开，方便你判断信息真伪。",
    cover:
      "https://readdy.ai/api/search-image?query=warm%20editorial%20flat%20illustration%20cream%20vermilion%20amber%20palette%2C%20two%20gift%20box%20packages%20with%20teal%20and%20brick%20design%20on%20a%20research%20board%2C%20colored%20threads%20connecting%20notes%20and%20pins%20around%20the%20boxes%2C%20tiny%20melon%20slices%2C%20paper%20texture%2C%20magazine%20infographic%2C%20clean%2C%20high%20detail&width=900&height=675&seq=ev-banxing-01&orientation=portrait",
    coverAlt: "半醒联名云梯国潮包装争议的事件地图封面插画",
    tags: ["国潮", "联名", "包装设计", "定价"],
    timeline: [
      {
        time: "上架首日",
        title: "联名礼盒开售，舆论升温",
        detail:
          "「半醒×云梯」联名礼盒全渠道开售，主打复古国潮风。有网友第一时间晒出礼盒包装，与「云梯」早年旧款包装的图案排布做对比，指出构图与配色「神似」。",
        tag: "网友讨论",
      },
      {
        time: "次日",
        title: "对比图在社交平台扩散",
        detail:
          "多组对比图被转发，话题登上实时热榜。讨论逐渐从「像不像」延伸到「老牌做旧款的时候为何无人关注」。",
        tag: "网友讨论",
      },
      {
        time: "第三天",
        title: "「半醒」发布说明",
        detail:
          "「半醒」官方账号发文，称本次视觉创意由委托的独立工作室完成，项目自研自查流程完整，对「抄袭」说法予以否认。",
        tag: "公告",
      },
      {
        time: "一周后",
        title: "工作室背景被挖出",
        detail:
          "网友发现该工作室早年确实为「云梯」旧系列服务过。舆论焦点转向「借鉴」与「致敬」的边界，双方均未再补充声明。",
        tag: "网友讨论",
      },
      {
        time: "两周后",
        title: "双方联合说明落地",
        detail:
          "「半醒」与「云梯」发布联合说明：产品照常销售，进一步完善授权与素材说明文档，感谢网友监督，未涉及下架。",
        tag: "公告",
      },
      {
        time: "现在",
        title: "话题转向「国潮定价」",
        detail:
          "联名礼盒 79 元的价格成为新的讨论点，「国潮该不该有溢价」在多个平台被反复讨论，事件进入「讨论层」。",
        tag: "媒体",
      },
    ],
    people: [
      {
        name: "半醒品牌方",
        role: "新中式茶饮",
        desc: "发起联名的一方，强调自研与授权流程，对「抄袭」指控予以否认。",
      },
      {
        name: "云梯市场部",
        role: "老牌零食",
        desc: "提供经典款与联名款，早前接受媒体采访时表示旧款早已停产多年。",
      },
      {
        name: "独立设计工作室",
        role: "创意外包方",
        desc: "被指此前服务过云梯旧系列，官方未直接公开回应。",
      },
      {
        name: "整理型网友",
        role: "舆论推动者",
        desc: "制作时间线与对比图，让散落信息第一次被集中呈现。",
      },
    ],
    perspectives: [
      {
        side: "支持半醒方",
        points: [
          "国潮视觉元素本就共用传统纹样，相似更多来自题材趋同而非抄袭。",
          "品牌方已按流程自查并委托外包，理应得到基本信任。",
        ],
      },
      {
        side: "质疑方",
        points: [
          "工作室历史履历与旧系列重叠，说明「独立原创」的说法需要更充分证据。",
          "先否认、后被发现关联，过程处理显得不够透明。",
        ],
      },
      {
        side: "从业设计师",
        points: [
          "创意行业「借鉴—致敬—抄袭」的边界长期模糊，行业需要更明确的素材授权规范。",
          "问题核心不在单个图案，而在外包链条的信息透明度。",
        ],
      },
      {
        side: "普通消费者",
        points: [
          "关心的是礼盒设计是否好看、价格是否值当，而非站队。",
          "79 元定价是否「国潮溢价」，比包装争议更贴近生活。",
        ],
      },
    ],
    netizens: [
      {
        nick: "@橘子汽水",
        text: "认真看完时间线才知道，吵的其实不是一件事，前三天吵抄袭，后两周吵定价。",
        likes: "1.2万",
        attitude: "中立",
      },
      {
        nick: "@在路上的鱼",
        text: "设计这行真的难，用了传统纹样就是抄，完全原创又像没根。边界太模糊。",
        likes: "8600",
        attitude: "中立",
      },
      {
        nick: "@一只铁锅",
        text: "我先不下结论，等工作室回应。做整理的平台也别急着给人定罪。",
        likes: "5400",
        attitude: "中立",
      },
      {
        nick: "@甜味雷达",
        text: "79 元的联名比单买还贵一截，这才是普通人最该关注的点吧。",
        likes: "3.1万",
        attitude: "反方",
      },
    ],
    sourceCheck: {
      verified: [
        "联名礼盒确于约定日期开售，价格为 79 元（官方商品页）。",
        "半醒官方确有发布否认「抄袭」的说明（官方账号留存）。",
        "云梯早前停产旧款系列，符合公开渠道信息。",
      ],
      unverified: [
        "工作室曾服务云梯旧系列的细节尚未获双方或工作室证实。",
        "「借鉴致利润受损」的具体损失数据未见可靠测算口径。",
        "网友对比图存在角度与取色差异，需以实物为准。",
      ],
    },
    related: ["zhibo-jianglai", "wanlu-dingjia"],
  },
  {
    slug: "changxiu-jiansha",
    title: "一档音综的「剪辑争议」事件地图",
    categorySlug: "yingshi",
    category: "影视娱乐",
    heat: 91,
    status: "基本落地",
    statusTone: "normal",
    readingMinutes: 6,
    updated: "2026-09-06",
    summary:
      "热播音综播出片段被观众指称「台词顺序被重排」引发剪辑争议。节目组两度回应，我们也把表演前后、正片与花絮逐一对应，还原争议真正围绕的剪辑伦理。",
    cover:
      "https://readdy.ai/api/search-image?query=warm%20editorial%20flat%20illustration%20cream%20vermilion%20amber%2C%20a%20film%20strip%20and%20clapperboard%20on%20a%20research%20board%20with%20threads%20linking%20pinned%20notes%20about%20a%20music%20show%20scene%2C%20paper%20texture%2C%20magazine%20infographic%2C%20clean%20composition%2C%20high%20detail&width=900&height=675&seq=ev-changxiu-01&orientation=portrait",
    coverAlt: "音综剪辑争议事件地图的封面插画",
    tags: ["综艺", "剪辑", "节目组回应", "花絮"],
    timeline: [
      {
        time: "正片上线",
        title: "淘汰结果公布引发讨论",
        detail:
          "某音综正片播出淘汰结果，落败选手的「告别发言」被部分观众认为与赛前情绪铺垫不符，弹幕出现「被剪过」的猜测。",
        tag: "网友讨论",
      },
      {
        time: "次日",
        title: "观众逐帧对照正片与花絮",
        detail:
          "有观众放出正片与未播花絮的时间码对照，指出两段发言的顺序与拼接痕迹明显。",
        tag: "网友讨论",
      },
      {
        time: "第三天",
        title: "节目组首次回应",
        detail:
          "节目组称播出版为「导演组基于节目时长与叙事节奏的正常剪辑」，否认恶意操纵赛果，但未回应具体对照片段。",
        tag: "回应",
      },
      {
        time: "第五天",
        title: "完整素材争议升级",
        detail:
          "话题聚焦「是否应公开完整母带」，多数讨论不再执着于输赢，而是转向真人秀剪辑的边界。",
        tag: "网友讨论",
      },
      {
        time: "一周后",
        title: "节目组二度说明",
        detail:
          "节目组补充说明剪辑原因、并公布部分花絮顺序以自证，事件热度随之回落。",
        tag: "公告",
      },
    ],
    people: [
      {
        name: "节目制作组",
        role: "综艺出品",
        desc: "负责成片剪辑与叙事，主张节目时长和节奏优先。",
      },
      {
        name: "对比取证网友",
        role: "观众自组织",
        desc: "制作时间码对照，推动「剪辑透明度」进入公共讨论。",
      },
      {
        name: "落败选手方",
        role: "参演嘉宾",
        desc: "未就剪辑单独发声，仅发文感谢观众支持。",
      },
    ],
    perspectives: [
      {
        side: "节目组视角",
        points: [
          "任何综艺都要做取舍，时长与节奏是行业通行做法。",
          "赛果由现场投票决定，剪辑不改变结果。",
        ],
      },
      {
        side: "观众视角",
        points: [
          "观众要的是「知情权」，而非要求公开全部素材。",
          "当发言顺序影响人设与情绪，就不仅仅是剪辑问题了。",
        ],
      },
      {
        side: "剪辑从业者",
        points: [
          "剪辑不是造假，但在涉及人设与结果时应标注「非现场原貌」。",
          "行业需要一套关于「叙事加工」的告知规范。",
        ],
      },
    ],
    netizens: [
      {
        nick: "@麦田里的守望",
        text: "我不在意谁晋级，我在意的是我们看到的到底是不是真的发生过的。",
        likes: "5.8万",
        attitude: "中立",
      },
      {
        nick: "@周末加班人",
        text: "终于有人把时间码扒出来了，这种整理对理清问题帮助太大了。",
        likes: "2.4万",
        attitude: "挺方",
      },
      {
        nick: "@不追星只吃瓜",
        text: "谁赢都行，节目组敢不敢把母带放出来大家自己看。",
        likes: "1.1万",
        attitude: "反方",
      },
    ],
    sourceCheck: {
      verified: [
        "淘汰结果与赛前公开票数一致（节目官方发布）。",
        "节目组曾两度发布回应说明（官方账号留存）。",
      ],
      unverified: [
        "「恶意篡改赛果」尚无证据支持，属于观众推测。",
        "对照片段仅来自部分平台截取，完整素材未公开，真伪待核。",
      ],
    },
    related: ["dianying-guanggao", "banxing-guochao"],
  },
  {
    slug: "zhibo-jianglai",
    title: "直播间「断货回锅」风波时间线",
    categorySlug: "pingtai",
    category: "平台热议",
    heat: 84,
    status: "等待回应",
    statusTone: "normal",
    readingMinutes: 5,
    updated: "2026-09-05",
    summary:
      "促销夜反复强调「最后一批、售完即下」的商品，隔日被网友发现重新上架且更低价。是补货还是饥饿营销？我们把每一次「下架」与「回归」标注在时间轴上。",
    cover:
      "https://readdy.ai/api/search-image?query=warm%20editorial%20flat%20illustration%20cream%20vermilion%20amber%2C%20live%20stream%20host%20screen%20with%20a%20shopping%20cart%20and%20a%20rising%20price%20tag%2C%20thread%20connecting%20pinned%20notes%20about%20restock%20incidents%2C%20paper%20texture%2C%20magazine%20infographic%2C%20clean%2C%20high%20detail&width=900&height=675&seq=ev-zhibo-01&orientation=portrait",
    coverAlt: "直播间断货回锅风波时间线封面插画",
    tags: ["直播", "饥饿营销", "补货", "价格"],
    timeline: [
      {
        time: "促销夜",
        title: "宣称「最后一批」",
        detail:
          "某头部直播间在促销夜多次强调某商品「最后一批、拍完就没了」，现场订单瞬间放量。",
        tag: "媒体",
      },
      {
        time: "次日",
        title: "商品被指再次上架",
        detail:
          "有网友发现同款商品在另一店铺以更低到手价开售，截图与链接被大量转发，「断货回锅」话题上热搜。",
        tag: "网友讨论",
      },
      {
        time: "第三天",
        title: "商家与平台各有说法",
        detail:
          "商家回应称是「跨店调货补库存」，主播团队则称「促销机制由商家设置」。两套说法并行，未有一方给出完整证据链。",
        tag: "回应",
      },
      {
        time: "现在",
        title: "等待平台监管口径",
        detail:
          "事件焦点转向：所谓「最后一批」的表述是否构成误导，平台是否需要对饥饿营销话术设限。",
        tag: "媒体",
      },
    ],
    people: [
      {
        name: "直播主播方",
        role: "导购主播",
        desc: "现场话术的讲述者，称机制由商家提供。",
      },
      {
        name: "商家",
        role: "品牌自营",
        desc: "解释为跨店调货补库存，非饥饿营销。",
      },
      {
        name: "下单网友",
        role: "消费者",
        desc: "担心「稀缺感话术」误导决策，要求平台给规则。",
      },
    ],
    perspectives: [
      {
        side: "主播视角",
        points: [
          "主播只对镜头负责，上架与补货权限在商家。",
          "「最后一批」来自商家给的文案包。",
        ],
      },
      {
        side: "消费者视角",
        points: [
          "用稀缺感诱导即时下单，本质是利用信息不对称。",
          "希望直播话术有可验证的标注。",
        ],
      },
      {
        side: "行业观察者",
        points: [
          "「清仓—补货」循环在直播渠道是普遍现象，监管仍在细化。",
          "判断是否误导，要看回放证据与价格口径。",
        ],
      },
    ],
    netizens: [
      {
        nick: "@今天也是穷鬼",
        text: "我信了「最后一批」冲动下单，第二天看到更低价，谁懂这种感觉。",
        likes: "6.6万",
        attitude: "反方",
      },
      {
        nick: "@理性剁手人",
        text: "先别急着骂，跨店调货确实存在。等商家把库存截图放出来再定论。",
        likes: "9800",
        attitude: "中立",
      },
      {
        nick: "@看戏的观众",
        text: "无论真相如何，「饥饿营销」四个字已经给直播行业提了个醒。",
        likes: "1.5万",
        attitude: "中立",
      },
    ],
    sourceCheck: {
      verified: [
        "促销夜确存在「最后一批」话术（直播回放留存）。",
        "同款商品确于次日在其他渠道以更低到手价上架（链接截图为证）。",
      ],
      unverified: [
        "「跨店调货」的库存流转证据尚未公开，属商家单方说法。",
        "「故意制造稀缺」的主观动机无法仅凭时间线证实。",
      ],
    },
    related: ["banxing-guochao", "wanlu-dingjia"],
  },
  {
    slug: "wanlu-dingjia",
    title: "网友发现的「羊毛价差」讨论合集",
    categorySlug: "shehui",
    category: "社会话题",
    heat: 79,
    status: "基本落地",
    statusTone: "cool",
    readingMinutes: 6,
    updated: "2026-09-04",
    summary:
      "同一件商品在不同账号、不同设备上被观察到不同到手价，引发「价格歧视」讨论。我们把典型案例、平台解释与实测方法整理成册，教你理性看待价差。",
    cover:
      "https://readdy.ai/api/search-image?query=warm%20editorial%20flat%20illustration%20cream%20vermilion%20amber%2C%20two%20mobile%20phones%20showing%20different%20price%20tags%20connected%20by%20a%20thread%2C%20magnifier%20and%20coins%20around%20pinned%20price%20notes%2C%20paper%20texture%2C%20magazine%20infographic%2C%20clean%2C%20high%20detail&width=900&height=675&seq=ev-wanlu-01&orientation=portrait",
    coverAlt: "羊毛价差讨论合集的封面插画",
    tags: ["价格", "价差", "比价", "消费"],
    timeline: [
      {
        time: "初始讨论",
        title: "同一商品现不同到手价",
        detail:
          "有用户晒出同一酒店、同一航班在不同设备上显示的到手价截图，称「看一遍就涨价」。",
        tag: "网友讨论",
      },
      {
        time: "发酵期",
        title: "平台解释「千人千面」",
        detail:
          "多家平台回应称推荐基于实时供需与账号优惠权益，价格随库存与券变化，并非针对个人抬价。",
        tag: "回应",
      },
      {
        time: "实测期",
        title: "网友发起双端比价实验",
        detail:
          "陆续有用户用新老账号、不同网络环境做对照实验，结论差异较大，「样本不足」成为共识。",
        tag: "网友讨论",
      },
      {
        time: "收敛期",
        title: "监管与指南跟进",
        detail:
          "讨论从「阴谋论」转向方法论：如何截图留证、如何区分「算法推荐」与「动态定价」。",
        tag: "媒体",
      },
    ],
    people: [
      {
        name: "发起实测的网友",
        role: "普通用户",
        desc: "用对照实验质疑价格透明性，是讨论的启动器。",
      },
      {
        name: "平台客服/法务",
        role: "平台方",
        desc: "解释价格由供需与权益决定，否认针对性抬价。",
      },
      {
        name: "消费研究者",
        role: "观察者",
        desc: "提醒区分「个性化推荐」与「价格歧视」的概念。",
      },
    ],
    perspectives: [
      {
        side: "平台方",
        points: [
          "价格实时变动源于库存与优惠权益，千人千面不等于抬价。",
          "用户看到的「涨价」多数是券失效或时段变化。",
        ],
      },
      {
        side: "消费者",
        points: [
          "信息不透明本身就会造成不信任，即使没有恶意抬价。",
          "希望平台提供「本商品价格组成」的可查依据。",
        ],
      },
      {
        side: "研究者",
        points: [
          "动态定价是商业常态，争议在「是否基于个人画像定价」。",
          "相比截图情绪，长期跟踪的价格样本才更接近真相。",
        ],
      },
    ],
    netizens: [
      {
        nick: "@比价小能手",
        text: "与其猜来猜去，不如养成比价习惯，货比三家永远不过时。",
        likes: "2.8万",
        attitude: "中立",
      },
      {
        nick: "@深夜下单员",
        text: "同一晚换个账号真的便宜 30 块，我录了屏。这就是真相。",
        likes: "4.3万",
        attitude: "反方",
      },
      {
        nick: "@算法从业者",
        text: "说句公道话，优先推荐自己人把价格显示靠前很常见，但直接改价个人是有风险的。",
        likes: "1.9万",
        attitude: "中立",
      },
    ],
    sourceCheck: {
      verified: [
        "多个用户截图显示不同账号存在到手价差异（样本存在）。",
        "平台确实发布过关于价格与权益的说明。",
      ],
      unverified: [
        "「针对个人连续抬价」缺乏系统化证据，多为单次截图。",
        "双端实测样本量小，结论不具统计意义。",
      ],
    },
    related: ["zhibo-jianglai", "banxing-guochao"],
  },
  {
    slug: "dianying-guanggao",
    title: "电影贴片广告引热议，片方致歉始末",
    categorySlug: "yingshi",
    category: "影视娱乐",
    heat: 72,
    status: "基本落地",
    statusTone: "normal",
    readingMinutes: 5,
    updated: "2026-09-03",
    summary:
      "一部新片映前贴片广告过长、且含「首映礼放映事故」无关内容，被观众吐槽「看广告入场」。片方从沉默到致歉并调整场次，我们还原这次公关应对的每一步。",
    cover:
      "https://readdy.ai/api/search-image?query=warm%20editorial%20flat%20illustration%20cream%20vermilion%20amber%2C%20a%20movie%20ticket%20and%20popcorn%20beside%20a%20screen%20with%20film%20ads%2C%20thread%20connecting%20pinned%20apology%20notes%2C%20paper%20texture%2C%20magazine%20infographic%2C%20clean%2C%20high%20detail&width=900&height=675&seq=ev-dianying-01&orientation=portrait",
    coverAlt: "电影贴片广告事件致歉始末的封面插画",
    tags: ["电影", "贴片广告", "致歉", "观影体验"],
    timeline: [
      {
        time: "上映首日",
        title: "贴片广告引发吐槽",
        detail:
          "观众反映新片映前广告接近 15 分钟，且多条与影片无关，观感被明显打断。",
        tag: "网友讨论",
      },
      {
        time: "两日后",
        title: "话题发酵，片方沉默",
        detail:
          "话题持续升温，片方未公开回应，部分影院接到观众退票与改场请求。",
        tag: "媒体",
      },
      {
        time: "第四日",
        title: "片方发布致歉声明",
        detail:
          "片方致歉并说明广告为发行渠道统一配置，宣布协调缩减映前广告时长、优化开场提示。",
        tag: "公告",
      },
      {
        time: "现在",
        title: "落地执行与制度讨论",
        detail:
          "各影院陆续调整。讨论转向「映前广告该不该有统一时长上限」。",
        tag: "媒体",
      },
    ],
    people: [
      {
        name: "片方",
        role: "出品发行",
        desc: "承认体验问题并致歉，推动场次与广告调整。",
      },
      {
        name: "影院排片方",
        role: "放映渠道",
        desc: "解释广告位由渠道统一投放，接受观众反馈。",
      },
      {
        name: "购票观众",
        role: "消费者",
        desc: "以实际观影体验发起讨论，诉求是明确的权益边界。",
      },
    ],
    perspectives: [
      {
        side: "片方/影院",
        points: [
          "映前广告是成熟商业模式，广告收入支持票价稳定。",
          "问题是「度」与「告知」，而非广告本身的存在。",
        ],
      },
      {
        side: "观众",
        points: [
          "买的是电影票，不是广告时段，超长广告侵占权益。",
          "开映时间不透明，让人无法安排时间。",
        ],
      },
      {
        side: "行业协会观察",
        points: [
          "海外部分地区有「映前广告时长与可退款」的惯例可参考。",
          "关键在给观众提前知情与选择的空间。",
        ],
      },
    ],
    netizens: [
      {
        nick: "@散场专家",
        text: "迟到 10 分钟都还在放广告，这才是最离谱的。",
        likes: "5.2万",
        attitude: "反方",
      },
      {
        nick: "@影院上班族",
        text: "讲真广告位不是影院单方面决定的，真别只骂我们。",
        likes: "7600",
        attitude: "中立",
      },
      {
        nick: "@会买早场票",
        text: "片方能一天内致歉并落地调整，这个态度值得给个赞。",
        likes: "1.3万",
        attitude: "挺方",
      },
    ],
    sourceCheck: {
      verified: [
        "映前广告确接近 15 分钟（多位观众与媒体现场记录）。",
        "片方已发布致歉声明并协调缩减时长（官方账号留存）。",
      ],
      unverified: [
        "广告收益「全部归属片方」的说法与渠道分成结构不符，缺乏依据。",
        "所谓「排片施压」未获双方证实。",
      ],
    },
    related: ["changxiu-jiansha", "banxing-guochao"],
  },
  {
    slug: "shanshi-renzhi",
    title: "AI 换脸短视频惹争议，平台出台新规",
    categorySlug: "pingtai",
    category: "平台热议",
    heat: 66,
    status: "热度回落",
    statusTone: "cool",
    readingMinutes: 6,
    updated: "2026-09-02",
    summary:
      "一条未标注的 AI 换脸视频被大量转发后引发「误导」争议。平台随后收紧深度合成内容标识规则。我们梳理换脸技术争议与「AI 水印」政策的来龙去脉。",
    cover:
      "https://readdy.ai/api/search-image?query=warm%20editorial%20flat%20illustration%20cream%20vermilion%20amber%2C%20robot%20and%20human%20face%20puzzle%20pieces%20joined%20by%20a%20thread%20on%20a%20board%2C%20pinned%20note%20about%20AI%20watermark%20rules%2C%20paper%20texture%2C%20magazine%20infographic%2C%20clean%2C%20high%20detail&width=900&height=675&seq=ev-shanshi-01&orientation=portrait",
    coverAlt: "AI换脸短视频与平台新规的封面插画",
    tags: ["AI", "深度合成", "内容标识", "平台规则"],
    timeline: [
      {
        time: "视频传播",
        title: "未标注换脸视频刷屏",
        detail:
          "一条以公众人物「虚拟替身」为主角的换脸视频被大量转发，相当比例观众误以为是真实现场。",
        tag: "媒体",
      },
      {
        time: "发酵",
        title: "「误导」争议被讨论",
        detail:
          "原视频发布者补充说明为 AI 生成，但传播链条已远早于说明，引发「内容可辨识性」的讨论。",
        tag: "网友讨论",
      },
      {
        time: "平台跟进",
        title: "平台收紧标识规则",
        detail:
          "多家平台更新规则，要求深度合成内容显著标注，违规账号将被限制与处罚。",
        tag: "公告",
      },
      {
        time: "现在",
        title: "话题转向技术监管边界",
        detail:
          "讨论聚焦：水印是否足够、平台识别技术如何落地、普通用户如何自我保护。",
        tag: "媒体",
      },
    ],
    people: [
      {
        name: "内容发布者",
        role: "创作者",
        desc: "以「整活」为目的制作，补充说明滞后于传播速度。",
      },
      {
        name: "视频平台",
        role: "内容治理方",
        desc: "出台深度合成内容标识与处罚新规。",
      },
      {
        name: "信息安全从业者",
        role: "技术观察",
        desc: "讲解合成检测、来源追踪与水印标准。",
      },
    ],
    perspectives: [
      {
        side: "创作者",
        points: [
          "AI 创作是新兴表达，标注即可，不应一刀切。",
          "传播失控的锅不该全由首发者背。",
        ],
      },
      {
        side: "平台治理",
        points: [
          "可辨识性是底线，标注义务应前置到发布环节。",
          "主动收紧规则，是为了避免监管的「事后铁拳」。",
        ],
      },
      {
        side: "普通网民",
        points: [
          "我们更需要「看到即怀疑」的媒介素养，而不只靠平台水印。",
          "遇到夸张内容先看来源、再转发。",
        ],
      },
    ],
    netizens: [
      {
        nick: "@AI观察员",
        text: "标注当然要，但普通人也得学会先问一句：这段是真的吗？",
        likes: "3.6万",
        attitude: "中立",
      },
      {
        nick: "@不惯着",
        text: "发布即标注，早干嘛去了，非得闹大了才补说明。",
        likes: "2.1万",
        attitude: "反方",
      },
      {
        nick: "@技术派",
        text: "现在检测模型进步很快，平台完全有能力在发布时自动加标识。",
        likes: "1.7万",
        attitude: "挺方",
      },
    ],
    sourceCheck: {
      verified: [
        "涉事视频未在发布时显著标注（传播记录可见）。",
        "多家平台已发布深度合成内容标识与治理规则。",
      ],
      unverified: [
        "「该视频系刻意制造误导」无法证实，更多是传播失序。",
        "对具体个人的负面影响量化数据暂未公开。",
      ],
    },
    related: ["changxiu-jiansha", "wanlu-dingjia"],
  },
];

export function getEventBySlug(slug: string): EventData | undefined {
  return events.find((e) => e.slug === slug);
}

export function getFeaturedEvents(): EventData[] {
  return events.filter((e) => e.statusTone === "hot").slice(0, 2);
}